/**
 * Collision-avoidance and vertical alignment layout algorithm for Google Docs-style
 * margin comment cards.
 *
 * Each comment card attempts to align its top coordinate with the corresponding
 * anchor highlight in the document canvas. When multiple comments cluster closely,
 * downward collision avoidance pushes subsequent cards down with a minimum gap
 * (stacking without overlapping). If a comment has no anchor in the document, it
 * is placed at the top or after the preceding positioned card.
 */

export interface CommentPositionTarget {
  id: string;
  idealTop: number;
  height: number;
}

export interface PositionedComment {
  id: string;
  top: number;
}

export const DEFAULT_GAP = 16;
export const DEFAULT_CARD_HEIGHT = 160;

/**
 * Computes non-overlapping vertical tops for margin comment cards.
 *
 * Sorts targets by idealTop first, ensuring cards flow naturally down the document.
 *
 * @param targets - Array of comment targets with their ideal vertical anchor offset and card height.
 * @param gap - Minimum vertical distance between adjacent cards (default: 16px).
 * @returns Map of comment ID to computed top pixel offset.
 */
export function computeCommentPositions(
  targets: CommentPositionTarget[],
  gap = DEFAULT_GAP,
): Map<string, number> {
  const result = new Map<string, number>();
  if (targets.length === 0) return result;

  const sorted = targets.slice().sort((a, b) => a.idealTop - b.idealTop);
  let currentBottom = 0;

  for (let i = 0; i < sorted.length; i++) {
    const target = sorted[i];
    const effectiveTop = Math.max(target.idealTop, currentBottom);
    result.set(target.id, effectiveTop);
    const height = target.height > 0 ? target.height : DEFAULT_CARD_HEIGHT;
    currentBottom = effectiveTop + height + gap;
  }

  return result;
}

/** Format an ISO date string for Google Docs-style timestamp display. */
export function formatCommentTimestamp(isoString: string): string {
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return "";
  return (
    d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) +
    " " +
    d.toLocaleDateString([], { month: "short", day: "numeric" })
  );
}
